import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials } from '@/mocks/anime';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <main className="pt-20 pb-10 md:pb-14 bg-background-50 min-h-screen">
      <SEO
        title="关于我们 - 51动漫官网 | 品牌介绍与联系方式"
        description="了解51动漫官网的品牌故事、愿景使命和核心价值。51动漫网与51动漫网站国产精品入口，致力于为动漫爱好者提供最优质的国产动漫内容平台。查看用户口碑与联系方式。"
        keywords="51动漫官网,51动漫,关于我们,品牌介绍,联系方式,51动漫网,51动漫网站"
        canonicalUrl="https://www.51anime.com/about"
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51anime.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <SchemaOrg type="Organization" data={{
        name: '51动漫官网',
        url: 'https://www.51anime.com/',
        description: '专注于国产动漫的综合性内容平台',
        email: 'contact@51anime.com',
        sameAs: [
          'https://space.bilibili.com/51anime',
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 51动漫官网',
        description: '了解51动漫官网的品牌故事、愿景使命和核心价值',
        url: 'https://www.51anime.com/about',
        isPartOf: { '@type': 'WebSite', name: '51动漫官网', url: 'https://www.51anime.com/' },
      }} />

      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-300 font-medium">关于我们</span>
        </nav>

        <section className="mb-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-500/10 text-primary-400">
              <i className="ri-ancient-gate-line text-3xl"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-3">
              关于51动漫官网
            </h1>
            <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
              51动漫官网是专注于国产动漫的综合性内容平台，致力于为动漫爱好者提供最新、最全面的动漫资讯与深度解析。我们汇聚了国产动漫领域的热门作品资讯、专业分析和社区讨论，是动漫爱好者不可错过的精品入口。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
            {[
              { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为国产动漫领域最具影响力的内容平台，让更多人发现和爱上国产动漫的精彩。' },
              { icon: 'ri-flag-line', title: '我们的使命', desc: '为国产动漫发声，传播优质动漫文化，连接创作者与观众，推动国产动漫产业发展。' },
              { icon: 'ri-heart-pulse-line', title: '核心价值', desc: '坚持原创内容、深度分析和真实评价，用专业视角解读每一部国产动漫作品。' },
            ].map((item) => (
              <div key={item.title} className="p-5 rounded-lg bg-background-100 border border-background-200/60 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-100 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading text-center mb-8">
            品牌口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-5 rounded-lg bg-background-100 border border-background-200/60">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" title={`${t.name} - 51动漫用户评价`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-100">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                  <div className="ml-auto flex items-center">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-secondary-500"></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-foreground-400 leading-relaxed">{t.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading text-center mb-8">
            联系我们
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@51anime.com' },
                { icon: 'ri-wechat-line', label: '微信公众号', value: '51动漫官方' },
                { icon: 'ri-bilibili-line', label: 'B站账号', value: '51动漫官方' },
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-lg bg-background-100 border border-background-200/60 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-100">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200/60">
              <p className="text-sm text-foreground-400 leading-relaxed text-center">
                如果您有任何问题、建议或合作意向，欢迎通过以上方式与我们联系。
                我们的团队将在1-3个工作日内回复您的消息。期待与每一位动漫爱好者的交流！
              </p>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}